import { Link } from "react-router-dom";
import { Radar, Mail, ShieldCheck, Lock, Sparkles } from "lucide-react";

const FOOTER_NAV = [
  {
    title: "Product",
    links: [
      { label: "Pricing", to: "/pricing" },
      { label: "Security", to: "/security" },
      { label: "About", to: "/about" },
      { label: "Sign in", to: "/login" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Terms of Service", to: "/terms" },
      { label: "Privacy Policy", to: "/privacy" },
      { label: "Refund Policy", to: "/refund" },
      { label: "AI Disclaimer", to: "/disclaimer" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Contact Support", to: "/contact" },
      { label: "support@radaracctintel.com", href: "mailto:support@radaracctintel.com" },
    ],
  },
];

export default function Footer({ compact = false }) {
  return (
    <footer
      data-testid="site-footer"
      className={`border-t border-[#1F1F1F] bg-[#0A0A0A] mt-16 ${compact ? "py-8" : "py-12 md:py-16"}`}
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-8">
        {!compact && (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 pb-10 md:pb-14 border-b border-[#1F1F1F]">
            {/* Brand column */}
            <div className="md:col-span-5">
              <Link to="/" className="inline-flex items-center gap-3 group">
                <div className="relative w-8 h-8 flex items-center justify-center rounded-sm border border-[#262626] bg-[#141414]">
                  <Radar className="w-4 h-4 text-[#0A84FF]" strokeWidth={1.6} />
                </div>
                <div>
                  <div className="font-display font-semibold text-white tracking-tight">RADAR</div>
                  <div className="kpi-label">Account Intelligence</div>
                </div>
              </Link>
              <p className="text-[13px] text-[#A3A3A3] leading-relaxed mt-5 max-w-md">
                AI-Powered Account Intelligence Assistant for B2B Growth Teams. Turn any company
                name into a 21-section executive intelligence report, deal opportunities, CXO
                outreach, and a CRM pipeline entry — in under 90 seconds.
              </p>
              <a
                href="mailto:support@radaracctintel.com"
                data-testid="footer-support-email"
                className="inline-flex items-center gap-2 mt-5 text-[13px] text-white hover:text-[#0A84FF] transition-colors group"
              >
                <Mail className="w-4 h-4 text-[#0A84FF]" strokeWidth={1.7} />
                <span className="underline-offset-4 group-hover:underline">support@radaracctintel.com</span>
              </a>

              <div className="flex flex-wrap items-center gap-3 mt-6 text-[10px] font-mono-data tracking-wider uppercase text-[#737373]">
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#262626] bg-[#141414]">
                  <ShieldCheck className="w-3 h-3 text-[#32D74B]" /> SSL Secure
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#262626] bg-[#141414]">
                  <Lock className="w-3 h-3 text-[#0A84FF]" /> Privacy-first
                </span>
                <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm border border-[#262626] bg-[#141414]">
                  <Sparkles className="w-3 h-3 text-[#FF9F0A]" /> AI-Native
                </span>
              </div>
            </div>

            {/* Link columns */}
            {FOOTER_NAV.map((col) => (
              <div key={col.title} className="md:col-span-2">
                <div className="kpi-label mb-4">/ {col.title.toUpperCase()}</div>
                <ul className="space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.href ? (
                        <a
                          href={l.href}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-[13px] text-[#A3A3A3] hover:text-white transition-colors"
                        >
                          {l.label}
                        </a>
                      ) : (
                        <Link
                          to={l.to}
                          data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          className="text-[13px] text-[#A3A3A3] hover:text-white transition-colors"
                        >
                          {l.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-6 text-[12px] text-[#525252] font-mono-data leading-relaxed">
          <div>
            © {new Date().getFullYear()} RADAR — Real-Time Account Intelligence &amp; Personalized Outreach Engine.
          </div>
          <div className="flex items-center gap-3">
            <span>Built for secure, AI-powered B2B account intelligence.</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
